import  { useEffect, useState } from 'react';
import Button from '../base/Button';
import logoSK from '../../assets/SK.png';

interface HeaderProps {
  user?: {
    name: string;
    role: string;
  };
  onLogout?: () => void;
}

const API_BASE = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:9080';

export default function Header({ user, onLogout }: HeaderProps) {
  const [me, setMe] = useState<{ name?: string | null; email?: string; isAdmin?: boolean; attributes?: string[] } | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setMe({ name: data?.name ?? null, email: data?.email, isAdmin: !!data?.isAdmin, attributes: Array.isArray(data?.attributes) ? data.attributes : [] });
        } else if (res.status === 401) {
          localStorage.removeItem('token');
        }
      } catch {
        // ignore network errors here; leave header fallback to props
      }
    })();
  }, []);

  // Attribute precedence and labels
  const ATTR_ORDER = ["Internal", "Investor", "Marketing", "Partner", "Public"] as const;
  const ATTR_DISPLAY: Record<string, string> = {
    Internal: "Internal",
    Investor: "Investor",
    Marketing: "Marketing",
    Partner: "Partner",
    Public: "Public User",
  };

  const primaryLabel = (attrs?: string[], isAdmin?: boolean): string => {
    if (isAdmin) return "Admin"; // keep explicit admin label when applicable
    if (!attrs || attrs.length === 0) return "User";
    for (const key of ATTR_ORDER) {
      if (attrs.includes(key)) return ATTR_DISPLAY[key];
    }
    return "User"; // fallback if unknown values
  };

  const displayName =
    (me?.name && String(me.name).trim()) ||
    me?.email ||
    user?.name ||
    'User';

  const role = me ? primaryLabel(me.attributes, me.isAdmin) : (user?.role ?? 'User');
  const showUser = Boolean(user || me);

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
  };

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <img src={logoSK} alt="SkyKove logo" className="w-12 h-12 rounded-lg object-cover" />
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-accent-600 to-accentAlt-600 bg-clip-text text-transparent">
                  SkyKove International
                </h1>
                <p className="text-sm text-slate-500">Virtual Data Room</p>
              </div>
            </div>
          </div>
          
          {showUser && (
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-slate-700">{displayName}</p>
                <p className="text-xs text-slate-500">{role}</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-r from-accent-100 to-accentAlt-100 rounded-full flex items-center justify-center">
                <i className="ri-user-line text-accent-600"></i>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout}
                className="ml-2"
              >
                <i className="ri-logout-box-line mr-2"></i>
                Logout
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
